/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import type { PortalPreferencesAddon } from '../../addons/portal-preferences-addon';
import type { PortalUser } from '../portal-user';
import type { PortalFeaturesAddon } from '../../addons/portal-features-addon';
import type { AppRouteItem, RouterStoreConfig, SideMenuGroup, SideMenuItem } from './interfaces';

export class RouterStore {

    @observable routes: AppRouteItem[];

    portalUser: PortalUser | null;
    portalFeatures: PortalFeaturesAddon | null;
    portalPreferences: PortalPreferencesAddon | null;
    getLoginPath: RouterStoreConfig['getLoginPath'];

    constructor(routerStoreConfig: RouterStoreConfig) {
        const {
            routes,
            portalUser = null,
            portalFeatures = null,
            portalPreferences = null,
            getLoginPath,
        } = routerStoreConfig;

        this.portalUser = portalUser;
        this.portalFeatures = portalFeatures;
        this.portalPreferences = portalPreferences;
        this.getLoginPath = getLoginPath;

        this.handleRoutes(routes);

        this.routes = routes;
        makeObservable(this);
    }

    getSideMenuContent(parentPath: string, items: AppRouteItem[]) {
        const navGroupsCache: Record<string, boolean> = {};
        const navGroups: SideMenuGroup[] = [];
        const navItems: SideMenuItem[] = [];

        items.forEach((item: AppRouteItem): void => {
            const {
                navGroupId,
                navGroupTitle,
                navGroupTitleKey,
                navIconSrc,
                navName,
                navNameKey,
                navTo,
                navClick,
                path,
                exact,
                triangleColor,
            } = item;

            if (navGroupId && typeof navGroupsCache[navGroupId] === 'undefined') {
                navGroupsCache[navGroupId] = true;

                navGroups.push({
                    groupId: navGroupId,
                    title: navGroupTitle,
                    titleKey: navGroupTitleKey,
                });
            }

            navItems.push({
                groupId: navGroupId,
                text: navName,
                textKey: navNameKey,
                iconSrc: navIconSrc,
                to: navTo ?? `${parentPath}/${path}`,
                onClick: navClick,
                exact,
                triangleColor,
            });
        });

        return {
            groups: navGroups,
            items: navItems,
        };
    }

    @action.bound
    hideRoute(routeId: string): void {
        this.callActionForEachRoute(this.routes, (route: AppRouteItem) => {
            if (route.id && route.id === routeId) {
                route.hidden = true;
            }
        });
    }

    @action.bound
    showRoute(routeId: string): void {
        this.callActionForEachRoute(this.routes, (route: AppRouteItem) => {
            if (route.id && route.id === routeId) {
                route.hidden = false;
            }
        });
    }

    @action
    protected handleRoutes(routes: AppRouteItem[]): void {
        this.callActionForEachRoute(routes, (route: AppRouteItem) => {
            if (typeof route.isRouteHidden === 'function') {
                route.hidden = route.isRouteHidden({
                    portalUser: this.portalUser,
                    portalFeatures: this.portalFeatures,
                    portalPreferences: this.portalPreferences,
                }) ?? false;
            } else {
                route.hidden = route.hidden ?? false;
            }
        });
    }

    @action
    protected callActionForEachRoute(routes: AppRouteItem[], action: (route: AppRouteItem) => void): void {
        routes.forEach((route) => {
            if (route.items?.length > 0) {
                this.callActionForEachRoute(route.items, action);
            }

            action(route);
        });
    }

}
